/**
 * SubtitlesV2.js — enhanced copy of Subtitles.js
 *
 * Additions over Subtitles.js:
 *  - followPlayhead: auto-scrolls Table to currentIndex when enabled
 *  - TimecodeNudgeButtons: ±100ms nudge on start/end times per row
 *  - InsertSubtitleButton: "+" button at bottom of each row to insert a new subtitle after it
 *  - Row height increased from 120px → 140px (desktop) to accommodate nudge buttons
 *
 * Unchnaged from Subtitles.js:
 *  - All keyboard shortcuts (Shift+Enter split, Arrow Up/Down navigation)
 *  - Click-to-seek on textarea
 *  - Dual-column mode rendering
 *  - Search match scrolling and highlighting
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import { Table } from 'react-virtualized';
import SubtitleIconLeftColumn from '../../../folders/subtitles/SubtitleIconLeftColumn';
import SubtitleIconRightColumn from '../../../folders/subtitles/SubtitleIconRightColumn';
import SubtitlesStyle from '../../../folders/subtitles/SubtitlesStyle';
import SubtitleTextarea from '../../../folders/subtitles/SubtitleTextArea';
import SubtitleTimeSection from '../../../folders/subtitles/SubtitleTimeSection';
import SubtitleTimeShiftButtons from '../../../folders/subtitles/SubtitleTimeShiftButtons';
import { getSubNodesfromVtt } from '../../../libs/readSub/SubUtils';
import logCollectionService from '../../../services/LogCollectionService';
import { fetchBlobUrl } from '../ReadBlobToString';
import InsertSubtitleButton from './InsertSubtitleButton';
import TimecodeNudgeButtons from './TimecodeNudgeButtons';

const ARROW_DOWN = 'ArrowDown';
const ARROW_UP = 'ArrowUp';

const ROW_HEIGHT_DESKTOP = 120; // matches .ReactVirtualized__Table__row { height: 120px !important } in SubtitlesStyle
const ROW_HEIGHT_MOBILE = 80;

export default function SubtitlesV2({
    currentIndex,
    subtitle,
    setSubtitle,
    checkSub,
    player,
    updateSub,
    updateSubIndex,
    translate,
    sub,
    removeSub,
    mergeSub,
    addSub,
    newSub,
    viewEng,
    bookmarked,
    setBookmarked,
    subtitleComment,
    setSubtitleComment,
    numEditors,
    selectedLng,
    setCurrentTime,
    leftColumnLanguage,
    rightColumnLanguage,
    showDualColumns,
    searchTerm,
    matchOffsets,
    currentMatchIndex,
    splitMirroredGujratiSubtitle,
    followPlayhead,
    readOnly,
}) {
    const isSeekDebugEnabled = useCallback(() => {
        try {
            return window?.localStorage?.getItem('subtitle_seek_debug') === '1';
        } catch (_e) {
            return false;
        }
    }, []);

    const logSeekDebug = useCallback(
        (...args) => {
            if (isSeekDebugEnabled()) {
                console.log('[SubtitleSeekDebug]', ...args);
                logCollectionService.logTaggedEvent(
                    'subtitle_seek_debug',
                    { args },
                    ['seek', 'subtitle-list', 'subtitle-editor'],
                );
            }
        },
        [isSeekDebugEnabled],
    );

    const [height, setHeight] = useState(100);
    const [anchorIndex, setAnchorIndex] = useState(null);
    const [isCommentBoxOpen, setCommentBoxOpen] = React.useState(false);
    const [commentText, setCommentText] = React.useState('');
    const [selectedSubtitle, setSelectedSubtitle] = useState(null);

    const [containerWidth, setContainerWidth] = useState(window.innerWidth - 40);
    const containerRef = useRef(null);
    const resizeListenerRef = useRef(null);
    const tableRef = useRef(null);

    const rowHeight = window.innerWidth < 768 ? ROW_HEIGHT_MOBILE : ROW_HEIGHT_DESKTOP;

    const resize = useCallback(() => {
        setHeight(window.innerHeight - 300);
        if (containerRef.current) {
            setContainerWidth(containerRef.current.offsetWidth);
        } else {
            setContainerWidth(window.innerWidth - 40);
        }
    }, []);

    const handleKeyPress = useCallback((e) => {
        if (e.ctrlKey && e.key.toLowerCase() === 's') {
            e.preventDefault();
            document.getElementById('savebtn')?.click();
        }
    }, []);

    const calculateCharsPerLine = function (textarea) {
        const textareaWidth = textarea.clientWidth;
        const tempElement = document.createElement('span');
        tempElement.style.position = 'absolute';
        tempElement.style.visibility = 'hidden';
        tempElement.style.fontSize = '16px';
        tempElement.style.fontFamily = window.getComputedStyle(textarea).fontFamily;
        tempElement.innerText = 'ABadi .';
        const innerTextWidth = tempElement.innerText.length;
        document.body.appendChild(tempElement);
        const charWidth = tempElement.clientWidth / innerTextWidth;
        document.body.removeChild(tempElement);
        return Math.floor(textareaWidth / charWidth);
    };

    const checkIfUpDownArrowNeedsToNavigateInside = useCallback((textarea, key) => {
        const charsPerLine = calculateCharsPerLine(textarea);
        const numberOfChars = textarea.value.length;
        const cursorPosition = textarea.selectionStart;

        if (cursorPosition === numberOfChars && key === ARROW_DOWN) {
            return false;
        }

        let cursorOnLine = Math.ceil(cursorPosition / charsPerLine);
        const newLineLengths = textarea.value.split('\n').map((line) => line.length);

        let lineLengths = [];
        for (let i = 0; i < newLineLengths.length; i++) {
            if (newLineLengths[i] <= charsPerLine) {
                lineLengths.push(newLineLengths[i]);
            } else {
                lineLengths.push(charsPerLine);
                lineLengths.push(newLineLengths[i] - charsPerLine);
            }
        }

        let startLine = 0;
        let endLine = lineLengths[0];
        for (let i = 0; i < lineLengths.length; i++) {
            if (cursorPosition > startLine && cursorPosition <= endLine) {
                cursorOnLine = i;
                break;
            }
            if (i < lineLengths.length - 1) {
                startLine = endLine + 1;
                endLine = endLine + lineLengths[i + 1];
            }
        }

        if (cursorOnLine < lineLengths.length && key === ARROW_DOWN) {
            return true;
        }
        if (cursorOnLine > 0 && key === ARROW_UP) {
            return true;
        }
        return false;
    }, []);

    const handleEditableListKeyDown = useCallback((e) => {
        if (e.target.matches('.itemsalist textarea')) {
            let currentNode = e.target.closest('.itemsalist');
            let currentElement = currentNode.querySelector('textarea[data-field="text2"]');

            const insideTextAreaOnly = checkIfUpDownArrowNeedsToNavigateInside(currentElement, e.key);
            if (insideTextAreaOnly) {
                currentElement.click();
                return;
            }

            // Use class-based index instead of DOM siblings — siblings don't exist for
            // rows outside the viewport in a virtualized list (react-virtualized unmounts them).
            const itemClass = [...currentNode.classList].find((c) => /^item_\d+$/.test(c));
            if (!itemClass) return;
            const currentIdx = parseInt(itemClass.replace('item_', ''), 10) - 1; // 0-based

            if (e.key === ARROW_DOWN && currentIdx < subtitle.length - 1) {
                const nextIdx = currentIdx + 1;
                tableRef.current?.scrollToRow(nextIdx);
                setTimeout(() => {
                    const nextEle = document.querySelector(
                        `#editable-list-v2 .item_${nextIdx + 1} textarea[data-field="text2"]`,
                    );
                    if (nextEle) {
                        nextEle.focus();
                        nextEle.setSelectionRange(0, 0);
                        nextEle.click();
                    }
                }, 50);
            }

            if (e.key === ARROW_UP && currentIdx > 0) {
                const prevIdx = currentIdx - 1;
                tableRef.current?.scrollToRow(prevIdx);
                setTimeout(() => {
                    const prevEle = document.querySelector(
                        `#editable-list-v2 .item_${prevIdx + 1} textarea[data-field="text2"]`,
                    );
                    if (prevEle) {
                        prevEle.focus();
                        prevEle.setSelectionRange(prevEle.value.length, prevEle.value.length);
                        prevEle.click();
                    }
                }, 50);
            }
        }
    }, [subtitle.length, checkIfUpDownArrowNeedsToNavigateInside]);

    const removeLocalStorageItem = useCallback(() => {
        localStorage.removeItem('subtitle');
    }, []);

    useEffect(() => {
        resize();
        const debounceResize = debounce(resize, 500);
        resizeListenerRef.current = debounceResize;
        window.addEventListener('resize', debounceResize);
        const editorElement = document.querySelector('#editable-list-v2');
        if (editorElement) {
            editorElement.addEventListener('keydown', handleEditableListKeyDown);
        }
        window.addEventListener('beforeunload', removeLocalStorageItem);
        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
            window.removeEventListener('resize', debounceResize);
            if (editorElement) {
                editorElement.removeEventListener('keydown', handleEditableListKeyDown);
            }
            window.removeEventListener('beforeunload', removeLocalStorageItem);
        };
    }, [resize, handleKeyPress, handleEditableListKeyDown, player, removeLocalStorageItem]);

    const timeToSeconds = useCallback((time) => {
        if (typeof time !== 'string') return Number.NaN;
        const normalized = time.trim().replace(',', '.');
        const parts = normalized.split(':');
        if (parts.length !== 3) return Number.NaN;
        const [hours, minutes, seconds] = parts.map(Number);
        if (!Number.isFinite(hours) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) {
            return Number.NaN;
        }
        return hours * 3600 + minutes * 60 + seconds;
    }, []);

    const secondsToTime = useCallback((seconds) => {
        const hours = Math.floor(seconds / 3600).toString().padStart(2, '0');
        const minutes = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
        const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
        const millisecs = Math.floor((seconds % 1) * 1000).toString().padStart(3, '0');
        return `${hours}:${minutes}:${secs}.${millisecs}`;
    }, []);

    function moveBeforeEnd(time, subtitleEndTime) {
        if (time === subtitleEndTime) {
            return time - 0.001;
        }
        return time;
    }

    const handleOnMouseClick = useCallback(
        (e, index, field, playerArg, subtitleArg) => {
            if (field === 'text' || field === 'text2') {
                if (!isCommentBoxOpen) {
                    const videoElement = playerArg.element;
                    if (videoElement) {
                        logSeekDebug('click', { index, field });

                        if (videoElement.paused) {
                            videoElement.pause();
                        } else {
                            videoElement.play();
                        }

                        const text = e.target.value;
                        const startTime = subtitleArg[index].start;
                        const endTime = subtitleArg[index].end;
                        const startInSeconds = timeToSeconds(startTime);
                        const endInSeconds = timeToSeconds(endTime);

                        if (
                            Number.isFinite(startInSeconds) &&
                            Number.isFinite(endInSeconds) &&
                            endInSeconds >= startInSeconds &&
                            playerArg.element.duration >= startInSeconds
                        ) {
                            const caretOffset = e.target.selectionStart;
                            let percentageBeforeCaret = text.length > 0 ? caretOffset / text.length : 0.5;
                            const totalDuration = endInSeconds - startInSeconds;
                            const durationBeforeCaret = totalDuration * percentageBeforeCaret;
                            const useNewTime = moveBeforeEnd(startInSeconds + durationBeforeCaret, endInSeconds);
                            if (Number.isFinite(useNewTime) && useNewTime >= 0) {
                                playerArg.updateTime(useNewTime);
                            }
                        }
                    }
                }
            }
        },
        [isCommentBoxOpen, logSeekDebug, timeToSeconds],
    );

    const handleKeyDown = useCallback(
        (e, index, field, playerArg, subtitleArg, selectedLanguageArg) => {
            if (e.shiftKey && e.key === 'Enter') {
                e.preventDefault();
                if (field === 'text' || field === 'text2') {
                    if (selectedLanguageArg === 'English') {
                        const text = e.target.value;
                        const caretOffset = e.target.selectionStart;
                        const newTextBefore = text.substring(0, caretOffset).trim();
                        const newTextAfter = text.substring(caretOffset).trim();
                        let percentageBeforeCaret = text.length > 0 ? caretOffset / text.length : 0.5;

                        const startTime = subtitleArg[index].start;
                        const endTime = subtitleArg[index].end;
                        const startInSeconds = timeToSeconds(startTime);
                        const endInSeconds = timeToSeconds(endTime);
                        const totalDuration = endInSeconds - startInSeconds;

                        const durationBeforeCaret = totalDuration * percentageBeforeCaret;
                        const newEndBefore = secondsToTime(startInSeconds + durationBeforeCaret);
                        const newStartAfter = secondsToTime(startInSeconds + durationBeforeCaret);

                        const newItems = [
                            ...subtitleArg.slice(0, index),
                            { ...subtitleArg[index], text: newTextBefore, text2: newTextBefore, end: newEndBefore },
                            {
                                ...subtitleArg[index],
                                text: newTextAfter,
                                text2: newTextAfter,
                                start: newStartAfter,
                                end: endTime,
                                note: null,
                                bookmark: false,
                            },
                            ...subtitleArg.slice(index + 1),
                        ];

                        playerArg.updateTime(startInSeconds);
                        setSubtitle(newItems);
                        splitMirroredGujratiSubtitle(index);
                    } else {
                        const text = e.target.value;
                        const caretOffset = e.target.selectionStart;
                        const newTextBefore = text.substring(0, caretOffset).trim();
                        const newTextAfter = text.substring(caretOffset).trim();
                        let percentageBeforeCaret = text.length > 0 ? caretOffset / text.length : 0.5;

                        const startTime = subtitleArg[index].start;
                        const endTime = subtitleArg[index].end;
                        const startInSeconds = timeToSeconds(startTime);
                        const endInSeconds = timeToSeconds(endTime);
                        const totalDuration = endInSeconds - startInSeconds;

                        const durationBeforeCaret = totalDuration * percentageBeforeCaret;
                        const newEndBefore = secondsToTime(startInSeconds + durationBeforeCaret);
                        const newStartAfter = secondsToTime(startInSeconds + durationBeforeCaret);
                        
                        const newItems = [
                            ...subtitleArg.slice(0, index),
                            {
                                ...subtitleArg[index],
                                text: newTextBefore,
                                text2: newTextBefore,
                                end: newEndBefore,
                                Etext: subtitleArg[index].Etext,
                                Etext2: subtitleArg[index].Etext2,
                            },
                            {
                                ...subtitleArg[index],
                                text: newTextAfter,
                                text2: newTextAfter,
                                start: newStartAfter,
                                end: endTime,
                                Estart: '00:00:00',
                                Eend: '00:00:00',
                                Etext: '',
                                Etext2: '',
                            },
                            ...subtitleArg.slice(index + 1),
                        ];

                        playerArg.updateTime(startInSeconds);
                        setSubtitle(newItems);
                    }
                }
            }
            // Prevent native textarea undo from racing with the app's Ctrl+Z handler
            // (window keydown listener). Without this, native undo fires onChange which
            // pushes a history entry, then undoSubs pops it — net result: no change.
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                return;
            }
            handleEditableListKeyDown(e);
        },
        [timeToSeconds, secondsToTime, setSubtitle, splitMirroredGujratiSubtitle, handleEditableListKeyDown],
    );

    const scrollTimeoutRef = useRef(null);

    // Follow playhead is handled via scrollToIndex prop (see getTargetScrollIndex below)

    // Search match scrolling
    useEffect(() => {
        if (currentMatchIndex === -1) return;
        const currentMatch = matchOffsets[currentMatchIndex];
        if (currentMatch) {
            const scrollToRow = currentMatch.textIndex;
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = setTimeout(() => {
                document.querySelector(`#editable-list-v2 .item_${scrollToRow + 1}`)?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }, 0);
        }
        return () => {
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        };
    }, [currentMatchIndex]);

    const highlightSearchTerm = (text, searchTerm, index) => {
        if (!searchTerm || !text) return text;
        const searchRegExp = new RegExp(searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        return text.replace(searchRegExp, (match) => match);
    };

    const handleTextChange = (index, newText) => {
        const newData = [...subtitle];
        if (newData[index]?.text2 != undefined) {
            newData[index].text2 = newText;
        }
        setSubtitle(newData);
    };

    const focusAndSelectMatch = useCallback(
        (matchIndex) => {
            if (!matchOffsets || !matchOffsets[matchIndex]) return;
            const match = matchOffsets[matchIndex];
            setTimeout(() => {
                const textarea = document.querySelector(
                    `#editable-list-v2 .item_${match.textIndex + 1} textarea[data-field="text2"]`,
                );
                if (textarea) {
                    textarea.focus();
                    textarea.setSelectionRange(match.start, match.end);
                    const row = document.querySelector(`#editable-list-v2 .item_${match.textIndex + 1}`);
                    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 50);
        },
        [matchOffsets],
    );

    useEffect(() => {
        if (currentMatchIndex >= 0 && matchOffsets && matchOffsets.length > 0) {
            focusAndSelectMatch(currentMatchIndex);
        }
    }, [currentMatchIndex, focusAndSelectMatch]);

    const handleSubtitleClick = useCallback((subtitleId) => {
        setSelectedSubtitle(subtitleId);
    }, []);

    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        };
    }, []);

    const processedSubtitles = useMemo(() => {
        return subtitle.map((sub) => ({ ...sub }));
    }, [subtitle]);

    // ResizeObserver: reacts to panel drags (not just window resize) for both width + height
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setContainerWidth(Math.floor(entry.contentRect.width));
                const tableH = Math.max(100, Math.floor(entry.contentRect.height) - 35);
                setHeight(tableH);
            }
        });
        ro.observe(el);
        // Initial values
        setContainerWidth(el.offsetWidth);
        setHeight(Math.max(100, el.clientHeight - 35));
        return () => ro.disconnect();
    }, []);

    // scrollToIndex prop: react-virtualized ONLY re-scrolls when this value changes.
    // Passing currentIndex directly means:
    //   - Video advances → currentIndex changes → prop changes → Grid scrolls ✓
    //   - Video paused at row 5 → currentIndex stays 5 → prop stays 5 →
    //     user edits/state changes don't trigger a re-scroll ✓
    // Search matches take priority over playhead.
    const getTargetScrollIndex = useMemo(() => {
        const matchIdx = matchOffsets[currentMatchIndex]?.textIndex;
        if (matchIdx !== undefined && matchIdx !== null) {
            return Math.min(Math.max(0, subtitle.length - 1), Math.max(0, matchIdx + 2));
        }
        if (followPlayhead && currentIndex != null && currentIndex >= 0) {
            return currentIndex;
        }
        return undefined;
    }, [currentMatchIndex, matchOffsets, subtitle.length, followPlayhead, currentIndex]);

    const getSubtitleDataForLanguage = useCallback(async (language) => {
        const videoProps = JSON.parse(localStorage.getItem('videoProps'));
        if (!videoProps?.videoLanguages) return [];
        const langIndex = videoProps.videoLanguages.findIndex((lang) => lang.languageName === language);
        if (langIndex === -1) return [];
        try {
            const vttUrl = videoProps.videoLanguages[langIndex].vttFileUrl;
            const vttData = await fetchBlobUrl(vttUrl);
            if (vttData) return getSubNodesfromVtt(vttData);
        } catch (error) {
            console.error(`Error fetching data for ${language}:`, error);
        }
        return [];
    }, []);

    const [leftColumnData, setLeftColumnData] = useState([]);
    const [rightColumnData, setRightColumnData] = useState([]);

    useEffect(() => {
        if (showDualColumns) {
            getSubtitleDataForLanguage(leftColumnLanguage).then(setLeftColumnData);
            getSubtitleDataForLanguage(rightColumnLanguage).then(setRightColumnData);
        }
    }, [leftColumnLanguage, rightColumnLanguage, showDualColumns, getSubtitleDataForLanguage]);

    const renderColumnHeaders = () => {
        if (showDualColumns) {
            return (
                <>
                    <div style={{ flex: '0 0 68px', maxWidth: '68px' }}></div>
                    <div style={{ flex: '0 0 95px', maxWidth: '95px', textAlign: 'center' }}>CPS/CPL</div>
                    <div style={{ flex: '0 0 25%', textAlign: 'center', color: '#ccc', fontSize: '14px' }}>
                        {leftColumnLanguage}
                    </div>
                    <div style={{ flex: '0 0 105px', maxWidth: '105px', textAlign: 'center' }}>CPS/CPL</div>
                    <div style={{ flex: '1', textAlign: 'center', fontWeight: 'bold', color: '#fff' }}>
                        {rightColumnLanguage} (Editable)
                    </div>
                </>
            );
        } else {
            return (
                <>
                    <div style={{ flex: '0 0 68px', maxWidth: '68px' }}></div>
                    <div style={{ flex: '0 0 105px', maxWidth: '105px', textAlign: 'center' }}>CPS/CPL</div>
                    <div style={{ flex: '0 0 90%', textAlign: 'center' }}>Original Text</div>
                </>
            );
        }
    };

    const renderSubtitleRow = (props) => {
        const rowData = props.rowData;
        const nextSub = subtitle[props.index + 1] || null;

        if (showDualColumns) {
            const leftSubtitle = leftColumnData[props.index];
            const rightSubtitle = rightColumnData[props.index];

            return (
                <div
                    key={props.key}
                    className={`${props.className} itemsalist item_${props.index + 1} focusinde_${props.index} subtitle-row-v2`}
                    style={{
                        ...props.style,
                        height: rowHeight,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'stretch',
                        overflow: 'visible',
                    }}
                >
                    <div className="item" style={{ width: '100%', display: 'flex' }}>
                        <SubtitleIconLeftColumn
                            index={props?.index}
                            rowData={rowData}
                            updateSubIndex={updateSubIndex}
                            mergeSub={mergeSub}
                            readOnly={readOnly}
                        />
                        <SubtitleIconRightColumn
                            index={props?.index}
                            rowData={rowData}
                            updateSub={updateSub}
                            removeSub={removeSub}
                            readOnly={readOnly}
                        />
                        <SubtitleTimeSection
                            rowData={rowData}
                            index={props?.index}
                            currentIndex={currentIndex}
                            viewEng={viewEng}
                            checkSub={checkSub}
                            handleKeyDown={handleKeyDown}
                            updateSub={updateSub}
                            startField={'start'}
                            endField={'end'}
                            startValue={leftSubtitle?.start}
                            endValue={leftSubtitle?.end}
                            disabled={true}
                        />
                        <div className="left-column-text" style={{ flex: '0 0 25%', padding: '2px', minWidth: 0 }}>
                            <textarea
                                style={{
                                    fontSize: '14px',
                                    width: '100%',
                                    height: rowHeight < 100 ? '70px' : '110px',
                                    resize: 'none',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    color: '#ccc',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '4px',
                                    padding: '6px',
                                    boxSizing: 'border-box',
                                    overflow: 'auto',
                                    wordWrap: 'break-word',
                                }}
                                value={leftSubtitle?.text2 || leftSubtitle?.text || ''}
                                readOnly
                                placeholder={`${leftColumnLanguage} text`}
                            />
                        </div>
                        <SubtitleTimeSection
                            rowData={rowData}
                            index={props?.index}
                            currentIndex={currentIndex}
                            viewEng={viewEng}
                            checkSub={checkSub}
                            handleKeyDown={handleKeyDown}
                            updateSub={updateSub}
                            startField={viewEng ? 'Estart' : 'start'}
                            endField={viewEng ? 'Eend' : 'end'}
                            startValue={viewEng ? rowData?.Estart : rowData?.start}
                            endValue={viewEng ? rowData?.Eend : rowData?.end}
                            disabled={readOnly}
                        />
                        <SubtitleTimeShiftButtons
                            index={props?.index}
                            rowData={rowData}
                            updateSub={updateSub}
                            subtitle={subtitle}
                            setCurrentTime={setCurrentTime}
                            bookmarked={bookmarked}
                            setBookmarked={setBookmarked}
                            subtitleComment={subtitleComment}
                            setSubtitleComment={setSubtitleComment}
                            isCommentBoxOpen={isCommentBoxOpen}
                            setCommentBoxOpen={setCommentBoxOpen}
                            commentText={commentText}
                            setCommentText={setCommentText}
                            selectedSubtitle={selectedSubtitle}
                            setSelectedSubtitle={setSelectedSubtitle}
                            handleSubtitleClick={handleSubtitleClick}
                            anchorIndex={anchorIndex}
                            setAnchorIndex={setAnchorIndex}
                            timeToSeconds={timeToSeconds}
                            secondsToTime={secondsToTime}
                            setSubtitle={setSubtitle}
                            readOnly={readOnly}
                        />
                        <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                            <SubtitleTextarea
                                index={props?.index}
                                rowData={rowData}
                                currentIndex={currentIndex}
                                player={player}
                                subtitle={subtitle}
                                updateSub={updateSub}
                                checkSub={checkSub}
                                highlightSearchTerm={highlightSearchTerm}
                                handleKeyDown={handleKeyDown}
                                handleOnMouseClick={handleOnMouseClick}
                                searchTerm={searchTerm}
                                matchOffsets={matchOffsets}
                                currentMatchIndex={currentMatchIndex}
                                handleTextChange={handleTextChange}
                                textField={viewEng ? 'Etext' : 'text'}
                                selectedLanguage={selectedLng}
                                readOnly={readOnly}
                            />
                        </div>
                    </div>

                    {/* Insert button inside row, at the bottom */}
                    {!readOnly && addSub && (
                        <InsertSubtitleButton
                            index={props.index}
                            sub={rowData}
                            nextSub={nextSub}
                            addSub={addSub}
                        />
                    )}
                </div>
            );
        }

        // Single-column mode — enhanced with nudge buttons
        return (
            <div
                key={props.key}
                className={`${props.className} itemsalist item_${props.index + 1} focusinde_${props.index} subtitle-row-v2`}
                style={{
                    ...props.style,
                    height: rowHeight,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'stretch',
                    overflow: 'visible',
                }}
            >
                <div className="item">
                    <SubtitleIconLeftColumn
                        index={props?.index}
                        rowData={rowData}
                        updateSubIndex={updateSubIndex}
                        mergeSub={mergeSub}
                        readOnly={readOnly}
                    />
                    <SubtitleIconRightColumn
                        index={props?.index}
                        rowData={rowData}
                        updateSub={updateSub}
                        removeSub={removeSub}
                        readOnly={readOnly}
                    />

                    {/* Time section with ±100ms nudge buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <TimecodeNudgeButtons
                            sub={rowData}
                            updateSub={updateSub}
                            player={player}
                            field="start"
                            readOnly={readOnly}
                        />
                        <SubtitleTimeSection
                            rowData={rowData}
                            index={props?.index}
                            currentIndex={currentIndex}
                            viewEng={viewEng}
                            checkSub={checkSub}
                            handleKeyDown={handleKeyDown}
                            updateSub={updateSub}
                            startField={viewEng ? 'Estart' : 'start'}
                            endField={viewEng ? 'Eend' : 'end'}
                            startValue={viewEng ? rowData?.Estart : rowData?.start}
                            endValue={viewEng ? rowData?.Eend : rowData?.end}
                            disabled={readOnly}
                        />
                        <TimecodeNudgeButtons
                            sub={rowData}
                            updateSub={updateSub}
                            player={player}
                            field="end"
                            readOnly={readOnly}
                        />
                    </div>

                    <SubtitleTimeShiftButtons
                        index={props?.index}
                        rowData={rowData}
                        updateSub={updateSub}
                        subtitle={subtitle}
                        setCurrentTime={setCurrentTime}
                        bookmarked={bookmarked}
                        setBookmarked={setBookmarked}
                        subtitleComment={subtitleComment}
                        setSubtitleComment={setSubtitleComment}
                        isCommentBoxOpen={isCommentBoxOpen}
                        setCommentBoxOpen={setCommentBoxOpen}
                        commentText={commentText}
                        setCommentText={setCommentText}
                        selectedSubtitle={selectedSubtitle}
                        setSelectedSubtitle={setSelectedSubtitle}
                        handleSubtitleClick={handleSubtitleClick}
                        anchorIndex={anchorIndex}
                        setAnchorIndex={setAnchorIndex}
                        timeToSeconds={timeToSeconds}
                        secondsToTime={secondsToTime}
                        setSubtitle={setSubtitle}
                        readOnly={readOnly}
                    />

                    <div style={{ flex: '1' }}>
                        <SubtitleTextarea
                            index={props?.index}
                            rowData={rowData}
                            currentIndex={currentIndex}
                            player={player}
                            subtitle={subtitle}
                            updateSub={updateSub}
                            checkSub={checkSub}
                            handleKeyDown={handleKeyDown}
                            highlightSearchTerm={highlightSearchTerm}
                            handleOnMouseClick={handleOnMouseClick}
                            searchTerm={searchTerm}
                            matchOffsets={matchOffsets}
                            currentMatchIndex={currentMatchIndex}
                            handleTextChange={handleTextChange}
                            textField="text2"
                            selectedLanguage={selectedLng}
                            readOnly={readOnly}
                        />
                    </div>
                </div>

                {/* Insert button inside row, at the bottom */}
                {!readOnly && addSub && (
                    <InsertSubtitleButton
                        index={props.index}
                        sub={rowData}
                        nextSub={nextSub}
                        addSub={addSub}
                    />
                )}
            </div>
        );
    };

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', flex: 1, minHeight: 0, height: '100%' }} ref={containerRef}>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    {renderColumnHeaders()}
                </div>
                <SubtitlesStyle className={`subtitles ${showDualColumns ? 'dual-column' : ''}`}>
                    <Table
                        id="editable-list-v2"
                        ref={tableRef}
                        headerHeight={20}
                        width={containerWidth}
                        height={height}
                        rowHeight={rowHeight}
                        rowCount={subtitle.length}
                        rowGetter={({ index }) => subtitle[index]}
                        scrollToIndex={getTargetScrollIndex}
                        scrollToAlignment="auto"
                        headerRowRenderer={() => null}
                        rowRenderer={renderSubtitleRow}
                    />
                </SubtitlesStyle>
            </div>
        </>
    );
}
